import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";

/**
 * DeliveryDetails - Reusable component to display delivery receiver info and images
 *
 * @param {Object} data - Delivery data object
 * @param {Array} data.signature_files - Array of signature file objects with file_id, file_path, file_name
 * @param {Array} data.other_files - Array of other delivery file objects
 * @param {string} data.delivery_receiver_name - Name of the person who received delivery
 * @param {string} data.delivery_receiver_phone - Phone number of the receiver
 * @param {Object} sx - Optional MUI sx prop for container styling
 */
const DeliveryDetails = ({ data, sx = {} }) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);

  const handleImageClick = (images, index) => {
    setCurrentImages(images);
    setSelectedImageIndex(index);
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? currentImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === currentImages.length - 1 ? 0 : prev + 1
    );
  };

  if (!data) return null;

  const hasSignatureFiles =
    data.signature_files && data.signature_files.length > 0;
  const hasOtherFiles = data.other_files && data.other_files.length > 0;
  const hasDeliveryImages = hasSignatureFiles || hasOtherFiles;
  const hasReceiverInfo =
    data.delivery_receiver_name || data.delivery_receiver_phone;

  if (!hasDeliveryImages && !hasReceiverInfo) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 2, ...sx }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
          }}
        >
          <PhotoLibraryIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            Delivery Details
          </Typography>
        </Box>

        {/* Receiver Information */}
        {hasReceiverInfo && (
          <Box
            sx={{
              bgcolor: "grey.50",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
              mb: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            >
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                color="text.secondary"
              >
                Received By
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {data.delivery_receiver_name && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Name:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {data.delivery_receiver_name}
                  </Typography>
                </Box>
              )}

              {data.delivery_receiver_phone && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Phone:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {data.delivery_receiver_phone}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Delivery Images */}
        {hasDeliveryImages && (
          <>
            {/* Signature Files */}
            {hasSignatureFiles && (
              <Box sx={{ mb: hasOtherFiles ? 2 : 0 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Signature ({data.signature_files.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    maxHeight: "216px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {data.signature_files.map((file, imgIndex) => (
                    <Box
                      key={file.file_id}
                      sx={{
                        width: 120,
                        height: 100,
                        cursor: "pointer",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "2px solid #e0e0e0",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          borderColor: "primary.main",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() =>
                        handleImageClick(
                          data.signature_files.map((f) => f.file_path),
                          imgIndex
                        )
                      }
                    >
                      <img
                        src={file.file_path}
                        alt={file.file_name || `Signature ${imgIndex + 1}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Other Files */}
            {hasOtherFiles && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {`Delivery Image${data.other_files.length > 1 ? "s" : ""}`} (
                  {data.other_files.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    maxHeight: "216px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {data.other_files.map((file, imgIndex) => (
                    <Box
                      key={file.file_id}
                      sx={{
                        width: 120,
                        height: 100,
                        cursor: "pointer",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "2px solid #e0e0e0",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          borderColor: "primary.main",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() =>
                        handleImageClick(
                          data.other_files.map((f) => f.file_path),
                          imgIndex
                        )
                      }
                    >
                      <img
                        src={file.file_path}
                        alt={file.file_name || `Delivery proof ${imgIndex + 1}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Image Viewer Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "12px",
          },
        }}
      >
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.2)",
            },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
          }}
        >
          {currentImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}
          <Box
            sx={{
              width: "100%",
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={currentImages[selectedImageIndex]}
              alt={`Delivery proof ${selectedImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 2,
                textAlign: "center",
              }}
            >
              Image {selectedImageIndex + 1} of {currentImages.length}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeliveryDetails;
